export type FaqRelated = { path: string; title: string };

export type FaqEntry = {
  a: readonly string[];
  q: string;
  related?: readonly FaqRelated[];
};

export type FaqGroup = {
  id: string;
  intro: string;
  items: readonly FaqEntry[];
  navLabel: string;
  title: string;
};

export const FAQ_GROUPS: readonly FaqGroup[] = [
  {
    id: 'faq-basics',
    intro: 'The questions almost everyone starts with.',
    items: [
      {
        a: [
          'There is no single price. What you pay depends on your home, how much energy you use, and the equipment you choose. As rough national context, many systems fall between $15,000 and $30,000 before incentives, but that is only a reference point.',
          'The more useful number is your own. On Aurora Marketplace you get an estimate modeled to your actual roof and your electric bill, built from real pricing supplied by local installers, so you see a figure made for your home instead of an average.',
        ],
        q: 'How much do solar panels cost?',
      },
      {
        a: [
          'For many homeowners, yes. If you own your home, get decent sun, and pay a meaningful electric bill, solar can lower what you pay for power for years.',
          'Whether it is worth it for you depends on your roof, your utility rates, and how you pay for the system. The Marketplace estimates your monthly savings, your payback period, and your projected 25-year savings from your own bill and roof, so you can judge the value with real numbers rather than a national average.',
        ],
        q: 'Are solar panels worth it?',
      },
      {
        a: [
          'Your savings depend on how much energy you use, your local utility rate, and your net metering rules. Many homeowners offset most of their electricity, but a small fixed connection charge usually remains, so a true $0 bill is possible but not guaranteed.',
          'Because your estimate is modeled from your actual monthly bill and your utility, it shows your projected monthly and 25-year savings rather than a generic promise.',
        ],
        q: 'How much will I save, and will solar eliminate my electric bill?',
        related: [
          {
            path: '/home-solar/blog/solar-101/rising-energy-bills-go-solar-2026',
            title: 'Rising energy bills? Why 2026 is the year to go solar',
          },
        ],
      },
      {
        a: [
          'Payback is the point where your savings have covered the cost of the system. For a cash purchase it commonly lands somewhere around 7 to 12 years, after which the electricity your panels make is essentially free for the rest of their life, often 25 years or more.',
          'Your Marketplace estimate calculates the payback period for your specific system and bill, so you do not have to guess.',
        ],
        q: 'What is the payback period on solar?',
      },
      {
        a: [
          'It depends on how much electricity you use and how much usable sunlight your roof gets. Rather than a rule of thumb, the Marketplace models your specific roof from satellite imagery, its size, orientation, and shading, then sizes a system to your energy use.',
          'You can also adjust the panel count yourself on each offer and watch the price update live, so you can see how a bigger or smaller system changes your numbers.',
        ],
        q: 'How many solar panels do I need?',
        related: [
          {
            path: '/home-solar/blog/solar-101/is-my-house-good-for-solar',
            title: 'Is my house a good candidate for solar?',
          },
        ],
      },
      {
        a: [
          'Panels turn sunlight into electricity your home uses in real time. They still produce on cloudy days, just less, and they do not produce at night.',
          'That is what net metering and batteries are for. They cover you when the sun is not shining, using the credit or stored power you banked earlier.',
        ],
        q: 'Do solar panels work at night or on cloudy days?',
        related: [
          {
            path: '/home-solar/blog/solar-101/do-solar-panels-work-at-night',
            title: 'Do solar panels work on cloudy days or at night?',
          },
        ],
      },
    ],
    navLabel: 'Cost & savings',
    title: 'Solar basics, cost & savings',
  },
  {
    id: 'faq-incentives',
    intro: 'One of the most-searched, and most-changed, topics in solar right now.',
    items: [
      {
        a: [
          'The 30% federal tax credit for homeowners who buy their own system (the Residential Clean Energy Credit, Section 25D) ended on December 31, 2025. If you purchase a system now with cash or a loan, you cannot claim that federal credit anymore.',
          "Although, you may still qualify for state, local, and utility incentives. While homeowners can't directly receive the federal tax credit anymore, installers are still eligible for certain business tax credits. Those savings often pass-through to you when you purchase solar through a lease or PPA payment option. Any incentives that apply to you are already reflected in the net cost and savings on your Aurora estimate. Because tax rules change and depend on your situation, it's always good to confirm what applies to you with one of our energy advocates.",
        ],
        q: 'Is the federal solar tax credit still available in 2026?',
        related: [
          {
            path: '/home-solar/blog/solar-101/solar-tax-credit-2026',
            title: "Did the solar tax credit expire in 2026? Here's what homeowners need to know",
          },
        ],
      },
      {
        a: [
          'With a lease or power purchase agreement (PPA), the company that owns the system, not you, claims the available business tax credit, and typically factors those savings into the rate you pay.',
          'You do not file for the credit yourself, so compare the total cost over the life of the agreement, not just the monthly payment. The Marketplace prices cash, loan, and PPA or lease for the same system side by side, which makes that comparison straightforward.',
        ],
        q: 'Do I still get the tax credit if I lease or sign a PPA?',
        related: [
          {
            path: '/home-solar/blog/energy-101/free-solar-panels-guide',
            title: 'Are "free solar panels" really free?',
          },
        ],
      },
      {
        a: [
          'Beyond federal policy, many states, utilities, and cities offer their own rebates, performance payments, and property or sales-tax exemptions, and they vary a lot by location.',
          'Applicable incentives are factored into the net figures on your estimate, so the savings you see already reflect them.',
        ],
        q: 'What rebates and incentives are available in my state?',
        related: [
          {
            path: '/home-solar/blog/solar-101/solar-incentives',
            title: 'A guide to solar rebates and incentives',
          },
          {
            path: '/home-solar/blog/energy-101/what-is-heehra-home-energy-rebate',
            title: 'HEEHRA home energy rebate explained',
          },
        ],
      },
    ],
    navLabel: 'Incentives & tax credit',
    title: 'Incentives & the federal tax credit',
  },
  {
    id: 'faq-paying',
    intro: 'Cash, loan, lease, PPA, or prepaid PPA. The choice that most affects your savings.',
    items: [
      {
        a: [
          'Buying with cash or a loan means you own the system and keep the long-term savings and any added home value. Leasing or a PPA means little to no money upfront, but the provider owns the panels and you pay for the power or equipment over time.',
          'As a rule of thumb, ownership saves more over the life of the system, while third-party options lower the upfront hurdle and maintenance costs. On the Marketplace, cash, loan, and PPA or lease are all priced for the same system at once, so you can switch between them and compare like for like.',
        ],
        q: 'Should I buy or lease solar panels?',
      },
      {
        a: [
          'With a lease, you pay a fixed monthly amount to use the system. With a PPA, you pay per kilowatt-hour for the power it actually produces. In both, a third party owns the panels and handles maintenance. Some providers also offer a prepaid PPA, where you pay for the power up front instead of monthly.',
          'For a PPA or lease, your estimate shows the monthly payment and any annual escalator, so you can see how the cost changes over time.',
        ],
        q: 'What is the difference between a solar lease and a PPA?',
        related: [
          {
            path: '/home-solar/blog/solar-101/solar-ppa-vs-lease',
            title: "What's the difference between a solar PPA and a solar lease?",
          },
        ],
      },
      {
        a: [
          "With a regular PPA, you pay for your system's power month by month, usually at a rate that can step up a little each year. With a prepaid PPA, you pay for that power once, up front, typically at a discount, and then have no monthly solar payment at all.",
          'In both cases the provider still owns the system and handles maintenance, so you get the worry-free side of a PPA with something closer to the economics of buying. Prepaid options are priced on your estimate where available, so you can compare them against cash, loan, and monthly PPA or lease for the same system.',
        ],
        q: 'What is a prepaid PPA, and how is it different from a regular PPA?',
        related: [
          {
            path: '/home-solar/blog/solar-101/solar-ppa-vs-lease',
            title: 'What is a prepaid solar lease?',
          },
        ],
      },
      {
        a: [
          '$0-down loans, leases, and PPAs are real and common. But "free" usually means you do not own the system and you are paying over time through your loan payment or power rate.',
          'Read the terms, look at the total cost over 20 to 25 years, and compare against buying. Because the Marketplace prices every payment option for the same system, you can see the full cost of "free" next to ownership before you decide.',
        ],
        q: 'Can I get solar with $0 down, and what is the catch with "free" solar?',
        related: [
          {
            path: '/home-solar/blog/energy-101/free-solar-panels-guide',
            title: 'The truth about "free solar"',
          },
        ],
      },
      {
        a: [
          'Loans are typically paid off at closing, often from the sale proceeds, or transferred to the buyer. Leases and PPAs are usually transferred to the buyer or bought out before the sale. A prepaid PPA transfers too, and it can be an easy sell since the power is already paid for.',
          'Planning to move soon? Factor that in before you sign.',
        ],
        q: 'What happens to my loan or lease if I sell the house?',
        related: [
          {
            path: '/home-solar/blog/solar-101/selling-a-house-with-solar-panels',
            title: 'Selling a house with solar panels',
          },
        ],
      },
    ],
    navLabel: 'Paying for solar',
    title: 'Paying for solar',
  },
  {
    id: 'faq-battery',
    intro: 'The part that surprises most homeowners.',
    items: [
      {
        a: [
          'On their own, no. For safety, a standard grid-tied system shuts off during an outage so it cannot send power into lines that utility crews may be working on.',
          'To keep your lights on when the grid goes down, you need a battery, or a compatible backup setup, that can safely run your home while disconnected from the grid.',
        ],
        q: 'Do solar panels work during a power outage?',
      },
      {
        a: [
          'Not necessarily. A battery is worth considering if you have frequent outages, your utility pays little for the power you export, or you simply want backup power. Otherwise, the grid effectively acts as your storage through net metering.',
          'On the Marketplace you can add battery storage to any offer and see it priced on its own, with its own financing, so it is easy to compare solar with and without a battery.',
        ],
        q: 'Do I need a battery with my solar panels?',
        related: [
          {
            path: '/home-solar/blog/energy-101/solar-battery',
            title: 'Solar batteries: how they work, cost, and whether you need one',
          },
        ],
      },
      {
        a: [
          "It depends on the battery's size and what you are running. Many homeowners size a battery to cover essentials like the fridge, lights, internet, and a few outlets for a day or more, rather than powering the whole house at once.",
        ],
        q: 'How long will a solar battery power my house?',
        related: [
          {
            path: '/home-solar/blog/energy-101/storing-solar-energy-everything-you-need-to-know',
            title: 'Solar energy storage: everything you need to know',
          },
        ],
      },
      {
        a: [
          'Batteries add resilience and let you use more of your own solar power, but they also add cost. The value depends on how often your power goes out, what your utility pays for exported energy, and how much backup matters to you. Adding a battery to your estimate lets you see the extra cost and payment before you decide.',
        ],
        q: 'Are solar batteries worth the cost?',
      },
    ],
    navLabel: 'Batteries & backup',
    title: 'Batteries & backup power',
  },
  {
    id: 'faq-netmetering',
    intro: 'How the grid credits the power you do not use.',
    items: [
      {
        a: [
          'In many areas, yes. Through net metering or net billing, the extra power your system sends to the grid earns you credits on your bill. How much each credit is worth depends on your state and utility.',
          'Because your estimate reflects your actual utility rate environment, the savings it shows already account for how your utility credits exported power.',
        ],
        q: 'Can I sell solar power back to the grid?',
        related: [
          {
            path: '/home-solar/blog/solar-101/net-metering',
            title: 'What you need to know about net metering',
          },
        ],
      },
      {
        a: [
          'When your panels make more power than you are using, the surplus flows to the grid and you are billed for the net, meaning your total usage minus what you sent back. Rules and credit values vary by utility, and some areas have shifted to lower net billing rates.',
        ],
        q: 'How does net metering work?',
      },
      {
        a: [
          'A true-up is the periodic settlement, often once a year, where your utility tallies the credits you earned against the power you pulled from the grid, then bills or credits you for the difference.',
        ],
        q: 'What is a true-up?',
      },
    ],
    navLabel: 'Net metering & bills',
    title: 'Net metering & your bill',
  },
  {
    id: 'faq-install',
    intro: 'From choosing an installer to switch-on.',
    items: [
      {
        a: [
          "The physical installation is often just a day or two. The full timeline from signing to switch-on is usually longer, commonly one to three months, because of design, permitting, and your utility's approval to operate.",
          'Once you connect with an installer through the Marketplace, they become your source for the exact timeline in your area.',
        ],
        q: 'How long does it take to install solar panels?',
      },
      {
        a: [
          'Generally it goes like this: you compare estimates and choose an installer, the installer performs a detailed site assessment and confirms the design, then comes permitting, installation, an inspection, and finally permission to operate (PTO) from your utility before you turn the system on.',
        ],
        q: 'What is the process of getting solar installed, step by step?',
      },
      {
        a: [
          "PTO is your utility's formal sign-off that your system is safe and approved to connect to the grid. In most places you cannot switch your system on until you have it, and it is a common reason installs feel like they are taking a while at the end.",
        ],
        q: 'What is permission to operate (PTO)?',
      },
      {
        a: [
          "Most installations require local permits, and your installer usually handles them for you. If you are in an HOA, check its rules early, though many states limit an HOA's ability to block solar outright.",
        ],
        q: 'Do I need a permit or HOA approval?',
      },
    ],
    navLabel: 'Installation',
    title: 'Installation & what to expect',
  },
  {
    id: 'faq-maintenance',
    intro: 'What owning solar actually asks of you.',
    items: [
      {
        a: [
          'Very little. Panels have no moving parts, rain handles most of the cleaning, and the main things to keep an eye on are overall performance and the inverter over time.',
        ],
        q: 'Do solar panels need maintenance?',
        related: [
          {
            path: '/home-solar/blog/solar-101/solar-panel-maintenance',
            title: 'What you need to know about solar panel maintenance',
          },
        ],
      },
      {
        a: [
          'Most panels are built to last 25 to 30 years or more, and typically come with a performance warranty guaranteeing a minimum output for around 25 years. They slowly lose a little efficiency each year rather than failing all at once.',
        ],
        q: 'How long do solar panels last?',
        related: [
          {
            path: '/home-solar/blog/solar-101/how-long-do-solar-panels-last',
            title: 'How long do solar panels last?',
          },
        ],
      },
      {
        a: [
          'Solar systems usually carry several: a product or equipment warranty, a performance warranty for guaranteed output over time, and a workmanship warranty from your installer. Terms vary, so ask each installer you compare.',
        ],
        q: 'What warranties come with solar?',
      },
      {
        a: [
          'Warranties typically cover defective equipment, and a reputable installer or the manufacturer handles the repair or replacement. It is one more reason it helps to choose an established, well-reviewed installer, which you can weigh on each Marketplace offer.',
        ],
        q: 'What happens if a panel stops working?',
      },
    ],
    navLabel: 'Maintenance & warranties',
    title: 'Maintenance, warranties & reliability',
  },
  {
    id: 'faq-selling',
    intro: 'What happens to your panels when you move.',
    items: [
      {
        a: [
          "Owned solar is generally viewed as an asset and can add to a home's value, since it lowers the next owner's energy costs. Leased systems are more nuanced, because the agreement has to transfer to the buyer.",
        ],
        q: 'Do solar panels increase home value?',
        related: [
          {
            path: '/home-solar/blog/solar-101/solar-panels-home-value',
            title: 'Do solar panels increase your home value?',
          },
        ],
      },
      {
        a: [
          'Yes. If you own the system, it usually stays with the home and becomes a selling point. If you lease or have a PPA, you will transfer the agreement to the buyer or buy it out. Planning ahead makes the sale smoother.',
        ],
        q: 'Can I sell my house with solar panels?',
        related: [
          {
            path: '/home-solar/blog/solar-101/selling-a-house-with-solar-panels',
            title: 'Selling a house with solar panels',
          },
        ],
      },
    ],
    navLabel: 'Selling your home',
    title: 'Selling a home with solar',
  },
  {
    id: 'faq-aurora',
    intro: 'Who is behind these answers, and how your estimates are made.',
    items: [
      {
        a: [
          'Aurora Solar is a solar technology company founded in 2013. Our software is used by more than 7,000 solar organizations, including 90% of the top U.S. residential solar companies, and more than 20 million solar projects have been designed on our platform.',
          'In other words, the technology the solar industry already relies on to design and price systems now helps homeowners shop for solar with the same confidence.',
        ],
        q: 'Who is Aurora Solar?',
      },
      {
        a: [
          'It is a free way to get personalized solar estimates and compare local installers on your own timeline. You enter your address, your monthly electric bill, and your utility, and the Marketplace generates estimates from multiple independent installers so you can compare real options side by side. No account or contact information is required to see them.',
          'The Marketplace also gives you the knowledge to make confident decisions at any stage of your journey, and connects you with other energy solutions that match where you are in your goals at the time, whether that is a battery add-on for a new system or maintenance and repairs for a system you already own.',
        ],
        q: 'What is the Aurora Marketplace?',
      },
      {
        a: [
          'Each estimate is modeled to your specific home, not a regional average. Your roof is analyzed from satellite imagery for its size, orientation, and shading to project how much energy a system would produce, and that is combined with your monthly bill and your utility rate.',
          'The dollar figures come from installer pricing local to you, applied to your modeled system, which is why two houses on the same street can see different estimates.',
        ],
        q: 'How does the Marketplace calculate my estimate?',
      },
      {
        a: [
          'They are well-grounded modeled projections meant for comparison, not binding quotes. Where there is uncertainty, key figures are shown as low and high ranges rather than a single false-precision number, and applicable incentives are already reflected in the net cost and savings.',
          'The installer you choose confirms the final design and price after an in-person site assessment, which checks things aerial imagery cannot, like roof condition and electrical panel capacity. A confirmed proposal may differ modestly from the estimate. A large difference is uncommon and worth questioning unless you and your installer have chosen to significantly change the system design.',
        ],
        q: 'How accurate are the estimates?',
      },
      {
        a: [
          'Yes. Cash, loan, and PPA or lease, plus prepaid arrangements where available, are all priced for the same system at once, so you can toggle between them and compare like for like. For each option your estimate shows the relevant details, such as monthly payment, estimated monthly and 25-year savings, and payback period for a cash purchase.',
          'You can also raise or lower the panel count and add battery storage on each offer, with the price updating live. These are adjustments to the estimate; the installer confirms the final design after a site visit.',
        ],
        q: 'Can I compare financing options and customize the system?',
      },
      {
        a: [
          'The Marketplace is free to use, and your estimates are generated anonymously. You can compare full estimates without creating an account or sharing your phone or email.',
          'Your contact details are shared only with the specific installer you choose to connect with, not with everyone on the page, and only at the moment you decide to reach out.',
        ],
        q: 'Is it really free, and will I get flooded with sales calls?',
      },
      {
        a: [
          "When you're ready, contact an installer and share your name, phone, and email. You'll confirm your information with a one-time passcode by text or email, so your details reach the right installer and no one else.",
          "Next, we'll call you. We'll talk through your system design, answer your questions, and set your installer up to give you a real number, not an estimate.",
          "You don't have to stop at one installer. Line up a few and compare offers side by side. We recommend it.",
        ],
        q: 'What happens after I connect with an installer?',
      },
      {
        a: [
          'Yes. Aurora Marketplace support can help you interpret your estimates and navigate the process, by phone Monday through Friday from 9:00 AM to 5:00 PM Mountain Time, by live chat, or by text. That support comes from the Marketplace, not the installer.',
        ],
        q: 'Can I talk to a real person if I need help?',
      },
    ],
    navLabel: 'Aurora & the Marketplace',
    title: 'Aurora & how the Marketplace works',
  },
  {
    id: 'faq-installer',
    intro: 'How to protect yourself and get a fair deal.',
    items: [
      {
        a: [
          "Compare more than price. Look at reviews and reputation, warranty terms, the equipment offered, and how clearly a company answers your questions. On the Marketplace, each installer's rating, BBB accreditation, and years in business sit right on their offer, so you can weigh them next to the numbers.",
          'Getting multiple quotes is the single best way to protect yourself and to negotiate.',
        ],
        q: 'How do I choose a solar company?',
      },
      {
        a: [
          'Watch for high-pressure sales, "free solar" claims that quietly hide a lease, promises of a specific tax benefit without anyone knowing your taxes, and quotes you cannot easily compare. Get everything in writing and compare at least a few offers.',
          'Comparing like-for-like estimates side by side, as the Marketplace is built to do, makes a bad offer easier to spot.',
        ],
        q: 'How do I avoid solar scams?',
        related: [
          {
            path: '/home-solar/blog/solar-101/solar-scams-to-avoid',
            title: 'Common solar scams to avoid',
          },
        ],
      },
      {
        a: [
          "That's up to you but we recommend more than one. Comparing quotes side by side is the clearest way to spot a fair price and a trustworthy installer, and the Marketplace lets you connect with several installers from one place to get competing offers.",
        ],
        q: 'How many quotes should I get?',
      },
    ],
    navLabel: 'Choosing an installer',
    title: 'Choosing a trustworthy installer',
  },
];

export const FAQ_ANSWER_TEXT = ({ a }: FaqEntry) => a.join(' ');

export const FAQ_BLOG_SLUGS = [
  '9-solar-truths-most-homeowners-learn-too-late',
  'solar-made-simple-guide',
  'rising-energy-bills-go-solar-2026',
] as const;
